import { useState } from "react";
import { MessageSquareWarning } from "lucide-react";
import { useMicrocopy, type MicrocopyKey } from "../lib/microcopy";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { notifySaveFailed, notifySaved } from "../lib/save";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Tooltip } from "./overlays";
import { ToggleGroup, ToggleGroupItem } from "./toggle-group";

export interface IssueReport {
  category: "Wording" | "Layout" | "Error" | "Suggestion" | "Other";
  description: string;
  path: string;
  caseNumber?: string;
  locale: string;
  viewport: string;
  screenshotPng?: string;
}

const CATEGORIES: Array<{ value: IssueReport["category"]; label: MicrocopyKey }> = [
  { value: "Wording", label: "reportIssueWording" },
  { value: "Layout", label: "reportIssueLayout" },
  { value: "Error", label: "reportIssueError" },
  { value: "Suggestion", label: "reportIssueSuggestion" },
  { value: "Other", label: "reportIssueOther" },
];

async function captureScreen(): Promise<string | undefined> {
  try {
    const { domToPng } = await import("modern-screenshot");
    return await domToPng(document.body, {
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      backgroundColor: "#ffffff",
      filter: (node) => !(node instanceof HTMLElement && node.dataset.reportIssueIgnore !== undefined),
    });
  } catch {
    return undefined;
  }
}

/**
 * "Report an issue" in the header of every screen: the picture of the page is taken before the dialog opens, so it shows what the user
 * saw; the user adds a line and a type, and the app's `onSubmit` posts it with the address, language and window size.
 */
export function ReportIssueButton({ onSubmit, className, labelled = false }: { onSubmit: (report: IssueReport) => Promise<unknown>; className?: string; labelled?: boolean }) {
  const m = useMicrocopy();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [shot, setShot] = useState<string | undefined>();
  const [attach, setAttach] = useState(true);
  const [category, setCategory] = useState<IssueReport["category"]>("Wording");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);

  const start = async () => {
    setCapturing(true);
    setShot(await captureScreen());
    setCapturing(false);
    setAttach(true);
    setDescription("");
    setOpen(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const answer = await onSubmit({
        category,
        description: description.trim(),
        path: window.location.pathname + window.location.search,
        caseNumber: document.querySelector("[data-case-number]")?.textContent?.trim() || undefined,
        locale: document.documentElement.lang || "ar",
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshotPng: attach ? shot : undefined,
      });
      if (notifySaved(answer, m("reportIssueSent"))) setOpen(false);
    } catch (e) {
      notifySaveFailed(e, m("reportIssueFailed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {labelled ? (
        <Button variant="ghost" size="sm" className={cn("gap-1.5", className)} onClick={start} loading={capturing} data-report-issue-ignore>
          {capturing ? null : <MessageSquareWarning />}
          <span className="hidden xl:inline">{m("reportIssue")}</span>
          <span className="sr-only xl:hidden">{m("reportIssue")}</span>
        </Button>
      ) : (
        <Tooltip content={m("reportIssue")}>
          <Button variant="ghost" size="icon-sm" className={className} onClick={start} loading={capturing} aria-label={m("reportIssue")} data-report-issue-ignore>
            {capturing ? null : <MessageSquareWarning />}
          </Button>
        </Tooltip>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md" data-report-issue-ignore>
          <DialogHeader>
            <DialogTitle>{m("reportIssueTitle")}</DialogTitle>
            <DialogDescription>{m("reportIssueHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <fieldset>
              <legend className="mb-1.5 text-sm font-medium">{m("reportIssueCategory")}</legend>
              <ToggleGroup type="single" variant="outline" className="flex-wrap" value={category} onValueChange={(v) => { if (v) setCategory(v as typeof category); }}>
                {CATEGORIES.map((c) => <ToggleGroupItem key={c.value} value={c.value}>{m(c.label)}</ToggleGroupItem>)}
              </ToggleGroup>
            </fieldset>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">{m("reportIssueDescription")}</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} dir="auto" autoFocus
                className="w-full rounded-lg border border-sand-300 bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </label>
            {shot ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} className="size-4 accent-aegold-600" />
                  {m("reportIssueScreenshot")}
                </label>
                {attach ? <img src={shot} alt="" className="max-h-40 w-full rounded-lg border border-sand-200 object-cover object-top" /> : null}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{m("cancel")}</Button>
            <Button onClick={send} loading={sending} disabled={!description.trim()}>{m("reportIssueSend")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
